import React from "react";
import classNames from "classnames";
import {
  CheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import CiroTooltipContainer from "../../shared/CiroTooltipContainer";
import { IImportProgress } from "./AutomationListImportContactsModal/useImportState";
import CiroSpinner from "../../shared/CiroSpinner";
import pluralize from "pluralize";

interface IImportStatusBannerProps {
  progress: IImportProgress;
  status: "processing" | "completed" | "failed";
  onClose?: () => void;
}

const MAX_ERRORS_TO_SHOW = 3;

const ImportStatusBanner: React.FC<IImportStatusBannerProps> = ({
  progress,
  status,
  onClose,
}) => {
  const { processedCount, totalCount, duplicateContacts, errors } = progress;
  const progressPercentage =
    totalCount === 0 ? 0 : Math.round((processedCount / totalCount) * 100);

  const getBannerContent = () => {
    switch (status) {
      case "processing":
        return (
          <div
            className={classNames(
              "flex",
              "items-center",
              "gap-3",
              "justify-start",
            )}
          >
            <div className={classNames("flex", "items-center", "flex-1")}>
              <CiroSpinner loading={true} />
              <span className={classNames("text-sm", "font-medium", "ml-2")}>
                Importing contacts... {progressPercentage}% complete
              </span>
            </div>
            <div
              className={classNames(
                "flex",
                "justify-center",
                "whitespace-nowrap",
              )}
            >
              <span className={classNames("text-xs", "text-gray-600")}>
                {processedCount} of {totalCount} processed
              </span>
              <div
                className={classNames(
                  "w-full",
                  "bg-blue-200",
                  "rounded-full",
                  "h-2",
                  "mt-1",
                )}
              >
                <div
                  className={classNames(
                    "bg-blue-500",
                    "h-2",
                    "rounded-full",
                    "transition-all",
                    "duration-300",
                  )}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
        );

      case "completed":
        const completedCount = progress.completedContacts.length;
        const duplicateCount = duplicateContacts.length;
        const updatedCount = progress.updatedContacts.length;
        const hasErrors = errors.length > 0;
        const hasIssues = hasErrors || duplicateCount > 0;

        const tooltipContent = hasIssues ? (
          <div className={classNames("text-sm", "max-w-xs")}>
            {hasErrors && (
              <div className={classNames("mb-2")}>
                <div
                  className={classNames("font-medium", "text-red-600", "mb-1")}
                >
                  Errors:
                </div>
                <div className={classNames("space-y-1")}>
                  {errors.slice(0, 3).map((error, index) => (
                    <div key={index} className={classNames("text-red-600")}>
                      {error}
                    </div>
                  ))}
                  {errors.length > 3 && (
                    <div className={classNames("text-red-600")}>
                      ... and {errors.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}
            {duplicateCount > 0 && (
              <div>
                <div
                  className={classNames(
                    "font-medium",
                    "text-yellow-600",
                    "mb-1",
                  )}
                >
                  Duplicates skipped: {duplicateCount}
                </div>
                <div className={classNames("space-y-1")}>
                  {duplicateContacts
                    .slice(0, MAX_ERRORS_TO_SHOW)
                    .map((contact, index) => (
                      <div
                        key={index}
                        className={classNames("text-yellow-600")}
                      >
                        {contact.firstName} {contact.lastName} -{" "}
                        {contact.companyName}
                      </div>
                    ))}
                  {duplicateCount > MAX_ERRORS_TO_SHOW && (
                    <div className={classNames("text-yellow-600")}>
                      ... and {duplicateCount - MAX_ERRORS_TO_SHOW} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null;

        const successContent = (
          <div className={classNames("flex", "items-center", "gap-3")}>
            <div className={classNames("flex-shrink-0")}>
              <CheckIcon
                className={classNames("h-5", "w-5", "text-green-600")}
              />
            </div>
            <div className={classNames("flex-1")}>
              <span
                className={classNames(
                  "text-sm",
                  "font-medium",
                  "text-green-800",
                )}
              >
                Import completed successfully! Added {completedCount}{" "}
                {pluralize("contact", completedCount)}
                {updatedCount > 0 && (
                  <span className={classNames("text-blue-700")}>
                    {" "}
                    ({updatedCount} {pluralize("contact", updatedCount)}{" "}
                    updated)
                  </span>
                )}
                {duplicateCount > 0 && (
                  <span className={classNames("text-yellow-700")}>
                    {" "}
                    ({duplicateCount} {pluralize("duplicate", duplicateCount)}{" "}
                    skipped)
                  </span>
                )}
                {hasErrors && (
                  <span className={classNames("text-red-700")}>
                    {" "}
                    ({errors.length} {pluralize("error", errors.length)})
                  </span>
                )}
              </span>
            </div>
            {hasIssues && (
              <ExclamationTriangleIcon
                className={classNames("h-4", "w-4", "text-yellow-500")}
              />
            )}
            {onClose && (
              <button
                onClick={onClose}
                className={classNames(
                  "flex-shrink-0",
                  "p-1",
                  "hover:bg-green-100",
                  "rounded-full",
                  "transition-colors",
                )}
              >
                <XMarkIcon
                  className={classNames("h-4", "w-4", "text-green-600")}
                />
              </button>
            )}
          </div>
        );

        return (
          <CiroTooltipContainer tooltip={tooltipContent} placement="bottom">
            {successContent}
          </CiroTooltipContainer>
        );

      case "failed":
        return (
          <div className={classNames("flex", "items-center", "gap-3")}>
            <div className={classNames("flex-shrink-0")}>
              <ExclamationTriangleIcon
                className={classNames("h-5", "w-5", "text-red-600")}
              />
            </div>
            <div className={classNames("flex-1")}>
              <span
                className={classNames("text-sm", "font-medium", "text-red-800")}
              >
                Import failed
                {errors.length > 0 && (
                  <span className={classNames("text-red-700")}>
                    {" "}
                    ({errors.length} {pluralize("error", errors.length)})
                  </span>
                )}
              </span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getBannerClassName = () => {
    switch (status) {
      case "processing":
        return classNames("bg-blue-50", "border-blue-200");
      case "completed":
        return classNames("bg-green-50", "border-green-200");
      case "failed":
        return classNames("bg-red-50", "border-red-200");
      default:
        return "";
    }
  };

  return (
    <div
      className={classNames(
        "border",
        "rounded-lg",
        "p-4",
        "mb-4",
        "w-full",
        "bg-white",
        getBannerClassName(),
      )}
    >
      {getBannerContent()}
    </div>
  );
};

export default ImportStatusBanner;
